import { api, apiFile } from "api/api";
import { AxiosError } from "axios";
import Pessoa from "model/administracao/cadastro/Pessoa";
import Usuario from "model/administracao/cadastro/Usuario";
import ManutencaoVeiculo from "model/gestao/frotas/ManutencaoVeiculo";
import Veiculo from "model/gestao/frotas/Veiculo";
import VeiculoItemInspecao from "model/gestao/frotas/VeiculoItemInspecao";
import Page from "model/page/Page";
import Pageable from "model/page/Pageable";
import { DataTableFilterMeta } from "primereact/datatable";
import { BaseServiceCadastros } from "service/BaseServiceCadastros";
import { getFilters } from "utils/filterUtils";
import { generateKey } from "utils/generateKey";

class ManutencaoVeiculoService implements BaseServiceCadastros<ManutencaoVeiculo> {

    pesquisar(page: Pageable, filters?: DataTableFilterMeta | null) {
        return new Promise<Page>((resolve, reject): void => {
            let url = `/manutencaoVeiculo/pesquisar`;
            url += getFilters(filters)
            const params = {
                page: page.page,
                size: page.size,
                sort: page.sort,
            };

            api
                .get(url, { params })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    consultar(id: number) {
        return new Promise<ManutencaoVeiculo>((resolve, reject): void => {
            api
                .get(`/manutencaoVeiculo/consultar/${id}`)
                .then(response => {
                    const manutencaoVeiculo: ManutencaoVeiculo = response.data;
                    manutencaoVeiculo.itens.forEach(item => {
                        item.key = generateKey();
                        return item;
                    });

                    resolve(manutencaoVeiculo)
                })
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    incluir(formData: FormData) {
        return new Promise<ManutencaoVeiculo>((resolve, reject): void => {
            apiFile
                .post(`/manutencaoVeiculo/incluir`, formData)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    editar(formData: FormData) {
        return new Promise<ManutencaoVeiculo>((resolve, reject): void => {
            apiFile
                .put(`/manutencaoVeiculo/editar`, formData)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    excluir(id: number) {
        return new Promise<void>((resolve, reject): void => {
            api
                .delete(`/manutencaoVeiculo/excluir/${id}`)
                .then(() => resolve())
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    finalizar(id: number) {
        return new Promise<void>((resolve, reject): void => {
            api
                .put(`/manutencaoVeiculo/finalizar/${id}`)
                .then(() => resolve())
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    reabrir(id: number) {
        return new Promise<void>((resolve, reject): void => {
            api
                .put(`/manutencaoVeiculo/reabrir/${id}`)
                .then(() => resolve())
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    buscarManutencaoPendente(idVeiculo: number) {
        return new Promise<ManutencaoVeiculo>((resolve, reject): void => {
            let url = `/manutencaoVeiculo/manutencaoPendente?idVeiculo=${idVeiculo}`;

            api
                .get(url)
                .then(response => {
                    const manutencaoVeiculo: ManutencaoVeiculo = response.data;
                    manutencaoVeiculo.itens.forEach(item => {
                        item.key = generateKey();
                        return item;
                    });
                    resolve(manutencaoVeiculo)
                })
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    empresasResponsaveis() {
        return new Promise<Pessoa[]>((resolve, reject): void => {
            api
                .get('/manutencaoVeiculo/empresasDisponiveis')
                .then((response) => resolve(response.data))
                .catch((error) => reject(error.response));
        });
    }

    getUsuarios() {
        return new Promise<Array<Usuario>>((resolve, reject): void => {
            api
                .get(`/manutencaoVeiculo/usuarios`)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    veiculosDisponiveis() {
        return new Promise<Veiculo[]>((resolve, reject): void => {
            api
                .get('/manutencaoVeiculo/veiculosDisponiveis')
                .then((response) => resolve(response.data))
                .catch((error) => reject(error.response));
        });
    }

    itensInspecaoDisponiveis(veiculo: Veiculo) {
        return new Promise<any[]>((resolve, reject): void => {
            api
                .get(`/manutencaoVeiculo/itensInspecaoDisponiveis/${veiculo.id}`)
                .then((response) => resolve(response.data))
                .catch((error) => reject(error.response));
        });
    }

    getRelatorio(filters: DataTableFilterMeta) {
        return new Promise<string>((resolve, reject): void => {
            let url = `/manutencaoVeiculo/report`;
            url += getFilters(filters);
            api
                .get(url, { responseType: 'arraybuffer' })
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    salvarNovoVeiculoItemInspecao(data: VeiculoItemInspecao) {
        return new Promise<VeiculoItemInspecao>((resolve, reject): void => {
            api
                .post(`/manutencaoVeiculo/salvarNovoVeiculoItemInspecao`, data)
                .then(response => resolve(response.data))
                .catch((err: AxiosError) => reject(err.response));
        });
    }

    tipos() {
        return [
            { label: 'Preventiva', value: 'PREVENTIVA' },
            { label: 'Corretiva', value: 'CORRETIVA' }
        ];
    }

    getTipo(tipo: string): string {
        if (tipo === "PREVENTIVA") return "Preventiva";
        if (tipo === "CORRETIVA") return "Corretiva";
        return "";
    }

    getStatus(status: string): string | undefined {
        if (status === "GERADA") return "Gerada";
        if (status === "EM_ANDAMENTO") return "Em andamento";
        if (status === "FINALIZADA") return "Finalizada";
        return "";
    }

    getStatusManutencaoVeiculoFilter() {
        return [
            { label: 'Gerada', value: 'G' },
            { label: 'Finalizada', value: 'F' },

        ]

    }

    getTipoManutencaoVeiculoFilter() {
        return [
            { label: 'Preventiva', value: 'P' },
            { label: 'Corretiva', value: 'C' },
        ];
    }

}

export default ManutencaoVeiculoService;